import ModeToggle from '@/components/common/ModeToggle';
import SearchModal from '@/components/layout/SearchModal';
import Sidebar from '@/components/layout/Sidebar'; // Import the Sidebar component
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/auth.service';
import { businessService } from '@/services/business.service';
import useApplyUITheme, { useSiteTheme } from '@/hooks/useApplyUITheme';
import {
  ChevronDown,
  FileText,
  GraduationCap,
  LogOut, Menu,
  // Palette, (Gradient Theme toggle is hidden)
  Plus,
  Search,
  Settings,
  User2,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

const getInitials = (name?: string, givenName?: string, familyName?: string): string => {
  if (givenName || familyName) {
    const initials = `${(givenName?.[0] || '').toUpperCase()}${(familyName?.[0] || '').toUpperCase()}`.trim();
    if (initials) return initials;
  }
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export default function DashboardLayout() {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Mobile sidebar
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    // Load collapsed state from localStorage
    const saved = localStorage.getItem('adminSidebarCollapsed');
    return saved === 'true';
  }); // Desktop sidebar collapse
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [gradientEnabled] = useState(() => {
    // Load gradient preference from localStorage
    const saved = localStorage.getItem('gradientTheme');
    return saved === 'true' || saved === null; // Default to true if not set
  });
  const { user } = useAuth();

  useEffect(() => {
    setImageError(false);
  }, [user?.profile_image]);

  // Apply the tenant's Admin UI customization (empty fields keep the default look)
  useApplyUITheme('admin');
  const { hasCustomSidebar } = useSiteTheme('admin');

// Fetch business settings for logos
  const { data: businessData } = useQuery({
    queryKey: ['businessSettings'],
    queryFn: () => businessService.getBusinessSettings(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  const companyName = businessData?.data?.company_name?.trim() || businessData?.company_name?.trim() || 'Skillzen';
  const largeLogoUrl = businessData?.data?.large_logo_url || businessData?.large_logo_url;
  const smallLogoUrl = businessData?.data?.small_logo_url || businessData?.small_logo_url;
  
  // Listen for storage changes from other components (like AILearnPage)
  useEffect(() => {
    const handleStorageChange = () => {
      const savedCollapsed = localStorage.getItem('adminSidebarCollapsed');
      setIsSidebarCollapsed(savedCollapsed === 'true');
    };
    
    // Listen for storage events
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Handle Cmd/Ctrl + K shortcut to open search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearchModal(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  // Toggle gradient theme - hidden for now along with its menu item
  // const toggleGradientTheme = () => {
  //   const newState = !gradientEnabled;
  //   setGradientEnabled(newState);
  //   localStorage.setItem('gradientTheme', newState.toString());
  // };
  
  // Save collapsed state to localStorage whenever it changes
  const toggleSidebarCollapsed = () => {
    const newState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newState);
    localStorage.setItem('adminSidebarCollapsed', newState.toString());
  };

    
  // Get user details from the auth context
  const userName = user ? `${user.given_name || ''} ${user.family_name || ''}`.trim() || user.full_name || 'User' : 'User';
  const userEmail = user?.email || 'email@example.com';
  const handleLogout = async () => {
    setIsLoggingOut(true);
    setShowProfileMenu(false);
    
    // Show loading toast
    const loadingToast = toast.loading('Logging out...');
    
    try {
      await authService.logout();
      toast.dismiss(loadingToast);
      toast.success('Logged out successfully');
      window.location.href = '/login';
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Logout failed. Please try again.');
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="min-h-screen bg-ui-bg">
      {/* Logout overlay */}
      {isLoggingOut && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-lg font-semibold text-gray-900">Logging out...</p>
            <p className="text-sm text-gray-500">Please wait while we sign you out securely</p>
          </div>
        </div>
      )}
      
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside className={`fixed top-0 left-0 h-full ${
        // A saved sidebar color replaces the default white / gradient panel
        hasCustomSidebar
          ? 'bg-ui-sidebar shadow-lg'
          : gradientEnabled
          ? 'bg-gradient-to-b from-white via-gray-50/50 to-gray-100/30 backdrop-blur-sm shadow-xl border-r border-gray-200/50'
          : 'bg-white shadow-lg'
        } transform transition-all duration-300 z-50 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 ${isSidebarCollapsed ? 'lg:w-20' : 'lg:w-64'} w-64`} style={{ overflow: 'visible' }}>
        <div className="flex flex-col h-full relative">
          {/* Subtle gradient overlay */}
          {gradientEnabled && !hasCustomSidebar && (
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 via-transparent to-purple-500/5 pointer-events-none" />
          )}
          <div className={`flex items-center justify-between p-4 border-b ${
              hasCustomSidebar
              ? 'border-ui-sidebar-text/15'
              : gradientEnabled
              ? 'border-gray-200/50 bg-gradient-to-r from-primary-50/50 to-purple-50/50'
              : 'border-gray-200'
            }`}>
            <Link
              to="/"
              // Ensure the Link takes full width and uses flex for alignment
              className={`flex items-center gap-2 ${isSidebarCollapsed ? 'lg:justify-center' : 'lg:justify-center w-full'
                }`}
            >
              {isSidebarCollapsed ? (
                // Collapsed → Small logo or GraduationCap
                smallLogoUrl ? (
                  <div className="w-12 h-12 flex items-center justify-center">
                    <img
                      src={smallLogoUrl}
                      alt={companyName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  // Follows the saved sidebar text color; default look keeps the brand color
                  <GraduationCap className={`w-8 h-8 ${hasCustomSidebar ? 'text-ui-sidebar-text' : 'text-primary-700'}`} />
                )
              ) : (
                // Expanded → Business logo or company name
                <div className={`flex items-center justify-center w-full h-16 px-2 rounded-md ${hasCustomSidebar ? '' : 'bg-gradient-to-r from-primary-50/50 to-purple-50/50'}`}>
                  {largeLogoUrl ? (
                    <img
                      src={largeLogoUrl}
                      alt={companyName}
                      className="w-full h-18 object-contain"
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-8 h-8 text-primary-700" />
                      <span className="text-xl font-bold text-gray-900">{companyName}</span>
                    </div>
                  )}
                </div>
              )}
            </Link>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className={`lg:hidden ${hasCustomSidebar ? 'text-ui-sidebar-text' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <Sidebar
            isOpen={isSidebarOpen}
            isCollapsed={isSidebarCollapsed}
            onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
            onClose={() => setIsSidebarOpen(false)}
            gradientEnabled={gradientEnabled}
          />
        </div>
      </aside>

      <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <header className="bg-white shadow-sm border-b sticky top-0 z-30">
          <div className="flex items-center justify-between px-3 lg:px-4 py-2 lg:py-3">
            <div className="flex items-center gap-4">
              {/* Desktop burger menu for sidebar toggle */}
              <button
                onClick={toggleSidebarCollapsed}
                className="hidden lg:block p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
              
              {/* Mobile burger menu */}
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
              
              <button 
                onClick={() => setShowSearchModal(true)}
                className="relative hidden md:flex items-center w-64 lg:w-96 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors text-left"
              >
                <Search className="w-5 h-5 text-gray-400 mr-3" />
                <span className="text-gray-500 text-sm truncate">Search</span>
              </button>
            </div>

            <div className="flex items-center gap-3">
              {/* Mode Toggle - Only shows for users with both roles */}
              <ModeToggle />

              <button
                onClick={() => window.open('/docs', '_blank')}
                className="hidden sm:flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 border border-gray-200 hover:border-indigo-200 rounded-lg transition-colors"
                title="Open documentation"
              >
                <FileText className="w-4 h-4" />
                <span className="text-sm font-medium">Docs</span>
              </button>

              {/* <button
                onClick={() => navigate('/admin/course/add-course')}
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm font-medium">New Course</span>
              </button> */}

              <NotificationBell />

              <div className="relative">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center gap-2 p-1 hover:bg-gray-100 rounded-lg"
                >
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-primary-600 text-white flex items-center justify-center text-xs font-semibold select-none flex-shrink-0">
                    {user?.profile_image && !imageError ? (
                      <img
                        src={user.profile_image}
                        alt={userName}
                        className="w-full h-full object-cover"
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <span>{getInitials(userName, user?.given_name, user?.family_name)}</span>
                    )}
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-gray-900">{userName}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>

                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border">
                    <div className="p-3 border-b">
                      <p className="text-sm font-medium text-gray-900">{userName}</p>
                      <p className="text-xs text-gray-500">{userEmail}</p>
                    </div>
                    <div className="p-2">
                      <Link
                        to="/admin/profile"
                        onClick={() => setShowProfileMenu(false)}
                        className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                      >
                        <User2 className="w-4 h-4" />
                        Profile
                      </Link>
                      <Link
                        to="/admin/settings/business"
                        onClick={() => setShowProfileMenu(false)}
                        className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </Link>
                      {/* Gradient Theme toggle - hidden for now */}
                      {/* <button
                        onClick={toggleGradientTheme}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Palette className="w-4 h-4" />
                          <span>Gradient Theme</span>
                        </div>
                        <div className={`w-9 h-5 rounded-full transition-colors ${gradientEnabled ? 'bg-primary-600' : 'bg-gray-300'
                          } relative`}>
                          <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${gradientEnabled ? 'translate-x-4' : 'translate-x-0'
                            }`} />
                        </div>
                      </button> */}
                      <button
                        onClick={handleLogout}
                        disabled={isLoggingOut}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${isLoggingOut
                            ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                            : 'text-red-600 hover:bg-red-50'
                          }`}
                      >
                        {isLoggingOut ? (
                          <>
                            <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                            Logging out...
                          </>
                        ) : (
                          <>
                            <LogOut className="w-4 h-4" />
                            Logout
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="ui-page p-4 lg:p-5 xl:p-6 overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      {/* Search Modal */}
      <SearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
      />
    </div>
  );
}
